import React, { useState, useEffect } from 'react';
import { AlertCircle, Save, X, Sparkles, Layers, ShieldCheck } from 'lucide-react';
import './EditSubjectForm.css';

/**
 * EditSubjectForm Component
 * 
 * Props:
 * @param {object} subject - Initial subject object to edit: { id, name, description, subjectType }
 * @param {function} onSave - Async or sync callback invoked with updated subject: (updatedData) => Promise<void>
 * @param {function} onCancel - Callback invoked when user clicks Cancel
 * @param {boolean} isSubmitting - External loading state flag (optional)
 */
export default function EditSubjectForm({
  subject,
  onSave,
  onCancel,
  isSubmitting = false,
}) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    subjectType: 'regular',
  });

  const [errors, setErrors] = useState({});

  // Sync state if subject prop changes
  useEffect(() => {
    if (subject) {
      setFormData({
        name: subject.name || '',
        description: subject.description || '',
        subjectType: subject.subjectType || 'regular',
      });
      setErrors({});
    }
  }, [subject]);

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Subject name is required';
    } else if (formData.name.trim().length < 3) {
      newErrors.name = 'Name must be at least 3 characters long';
    }

    if (formData.description && formData.description.length > 250) {
      newErrors.description = 'Description cannot exceed 250 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleTypeSelect = (type) => {
    setFormData((prev) => ({ ...prev, subjectType: type }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    if (onSave) {
      await onSave({
        ...subject,
        ...formData,
        name: formData.name.trim(),
        description: formData.description.trim(),
      });
    }
  };

  return (
    <form className="edit-subject-form" onSubmit={handleSubmit} noValidate>
      {/* Subject Name Field */}
      <div className="form-group">
        <label className="form-label" htmlFor="subject-name">
          Subject Name <span className="required-star">*</span>
        </label>
        <div className="input-wrapper">
          <input
            id="subject-name"
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="e.g. Science, Mathematics, English"
            className={`form-input ${errors.name ? 'form-input--error' : ''}`}
            disabled={isSubmitting}
          />
        </div>
        {errors.name && (
          <span className="field-error">
            <AlertCircle size={13} /> {errors.name}
          </span>
        )}
      </div>

      {/* Subject Type (Radio Segment Cards) */}
      <div className="form-group">
        <label className="form-label">Subject Classification</label>
        <div className="type-selector-grid">
          {/* Regular */}
          <button
            type="button"
            className={`type-card ${formData.subjectType === 'regular' ? 'type-card--active' : ''}`}
            onClick={() => handleTypeSelect('regular')}
            disabled={isSubmitting}
          >
            <div className="type-card__icon-box type-card__icon-box--regular">
              <Layers size={18} />
            </div>
            <div className="type-card__info">
              <span className="type-card__title">Regular</span>
              <span className="type-card__desc">Counts toward unlocking Side Quests</span>
            </div>
          </button>

          {/* Quests */}
          <button
            type="button"
            className={`type-card ${formData.subjectType === 'quests' ? 'type-card--active type-card--active-quests' : ''}`}
            onClick={() => handleTypeSelect('quests')}
            disabled={isSubmitting}
          >
            <div className="type-card__icon-box type-card__icon-box--quests">
              <Sparkles size={18} />
            </div>
            <div className="type-card__info">
              <span className="type-card__title">Side Quests</span>
              <span className="type-card__desc">Gated challenges (consumes 1 unlock)</span>
            </div>
          </button>

          {/* Foundation */}
          <button
            type="button"
            className={`type-card ${formData.subjectType === 'foundation' ? 'type-card--active' : ''}`}
            onClick={() => handleTypeSelect('foundation')}
            disabled={isSubmitting}
          >
            <div className="type-card__icon-box type-card__icon-box--foundation">
              <ShieldCheck size={18} />
            </div>
            <div className="type-card__info">
              <span className="type-card__title">Foundation</span>
              <span className="type-card__desc">Remedial or introductory fundamentals</span>
            </div>
          </button>
        </div>
      </div>

      {/* Description Field */}
      <div className="form-group">
        <div className="form-label-row">
          <label className="form-label" htmlFor="subject-description">
            Description <span className="optional-tag">(Optional)</span>
          </label>
          <span className="char-counter">{formData.description.length}/250</span>
        </div>
        <textarea
          id="subject-description"
          name="description"
          rows={3}
          value={formData.description}
          onChange={handleChange}
          placeholder="Short overview of what students will explore in this subject..."
          className={`form-textarea ${errors.description ? 'form-input--error' : ''}`}
          disabled={isSubmitting}
        />
        {errors.description && (
          <span className="field-error">
            <AlertCircle size={13} /> {errors.description}
          </span>
        )}
      </div>

      {/* Form Action Controls */}
      <div className="form-actions">
        <button
          type="button"
          className="btn btn--secondary"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          <X size={16} /> Cancel
        </button>

        <button
          type="submit"
          className="btn btn--primary"
          disabled={isSubmitting}
        >
          <Save size={16} />
          {isSubmitting ? 'Saving Changes...' : 'Save Subject'}
        </button>
      </div>
    </form>
  );
}